import { useDidUpdate } from '@mantine/hooks';
import { useMoonChainKeyParam } from 'hooks/useMoonChainParam';
import { useCallback } from 'react';
import { useRecoilState } from 'recoil';
import { proxiedAddressAtom } from 'recoil/proxiedAddress/atoms';
import { useAccount } from 'wagmi';

interface UseProxiedAddress {
  isEnabled: boolean;
  proxiedAddress: string;
  enable: VoidFunction;
  disable: VoidFunction;
  toggle: VoidFunction;
  setProxiedAddress: (address: string) => void;
}

export function useProxiedAddress(): UseProxiedAddress {
  const [{ isEnabled, proxiedAddress }, setAtom] =
    useRecoilState(proxiedAddressAtom);
  const moonChainKey = useMoonChainKeyParam();
  const { isConnected } = useAccount();

  const enable = useCallback(
    () => setAtom((prev) => ({ ...prev, isEnabled: true })),
    [setAtom],
  );

  const disable = useCallback(
    () => setAtom((prev) => ({ ...prev, isEnabled: false })),
    [setAtom],
  );

  const toggle = useCallback(
    () => setAtom((prev) => ({ ...prev, isEnabled: !prev.isEnabled })),
    [setAtom],
  );

  const setProxiedAddress = useCallback(
    (address: string) =>
      setAtom((prev) => ({ ...prev, proxiedAddress: address })),
    [setAtom],
  );

  useDidUpdate(disable, [moonChainKey]);
  useDidUpdate(
    () => setAtom({ isEnabled: false, proxiedAddress: '' }),
    [isConnected],
  );

  return {
    isEnabled,
    proxiedAddress: isEnabled ? proxiedAddress : '',
    enable,
    disable,
    toggle,
    setProxiedAddress,
  };
}
